import classNames from "classnames";
import { elementSize } from "features/option-controls/color-option-controls/DoorColorChoice";
import React from "react";
import Icon, { IconTypes } from "../icon";
import { DeBeursDeMaas } from "../icons/doorOptions/DeBeursDeMaas";
import { DeBeursDeWaal } from "../icons/doorOptions/DeBeursDeWaal";
import { DeBlaak } from "../icons/doorOptions/DeBlaak";
import { DeIjssel } from "../icons/doorOptions/DeIjssel";
import { DeKaap } from "../icons/doorOptions/DeKaap";
import { DeMaas } from "../icons/doorOptions/DeMaas";
import { DeMeent } from "../icons/doorOptions/DeMeent";
import { DeWaal } from "../icons/doorOptions/DeWaal";
import { Eigenwijs } from "../icons/doorOptions/Eigenwijs";
import { Cool } from "../icons/eigenwijsOptions/Cool";
import { DeBeurs } from "../icons/eigenwijsOptions/DeBeurs";
import { Populair3 } from "../icons/eigenwijsOptions/Populair3";
import { ZelfKiezen } from "../icons/eigenwijsOptions/ZelfKiezen";

export type DoorOptionsType =
  | "dekaap"
  | "deblaak"
  | "demeent"
  | "eigenwijs"
  | "populair_1"
  | "populair_2"
  | "populair_3"
  | "zelfkiezen"
  | "deijssel"
  | "deijssel1"
  | "dewaal"
  | "dewaal2"
  | "demaas"
  | "deijsseldubbel"
  | "deijssel1dubbel"
  | "dewaaldubbel"
  | "dewaal2dubbel"
  | "dewaal3dubbel"
  | "dewaal4dubbel"
  | "demaasdubbel"
  | "debeursdemaas"
  | "debeursdewaal";

interface DoorOptionsIconProps {
  selectedDoor: DoorOptionsType;
  isActive: boolean;
  className?: string;
}

/**
 * A mapping of door option types to their respective icons.
 */
const doorIconMapping: Record<DoorOptionsType, JSX.Element> = {
  dekaap: <DeKaap />,
  deblaak: <DeBlaak />,
  demeent: <DeMeent />,
  eigenwijs: <Eigenwijs />,
  populair_1: <DeBeurs />,
  populair_2: <Cool />,
  populair_3: <Populair3 />,
  zelfkiezen: <ZelfKiezen />,
  deijssel: <DeIjssel />,
  deijssel1: <DeIjssel />,
  dewaal: <DeWaal />,
  demaas: <DeMaas />,
  deijsseldubbel: <DeIjssel />,
  deijssel1dubbel: <DeIjssel />,
  dewaaldubbel: <DeWaal />,
  demaasdubbel: <DeMaas />,
  dewaal2: <DeWaal />,
  dewaal2dubbel: <DeWaal />,
  dewaal3dubbel: <DeWaal />,
  dewaal4dubbel: <DeWaal />,
  debeursdemaas: <DeBeursDeMaas />,
  debeursdewaal: <DeBeursDeWaal />,
};

/**
 * Renders an icon for a door option.
 * @param {DoorOptionsType} selectedDoor - The selected door option.
 * @param {boolean} isActive - Whether the door option is active or not.
 * @returns {JSX.Element} The icon element.
 */
const DoorOptionsIcon: React.FC<DoorOptionsIconProps> = ({
  selectedDoor = "dekaap",
  isActive = false,
  className,
}) => {
  const element = doorIconMapping[selectedDoor] ?? <DeKaap />;

  const clonedIcon = React.cloneElement(element, {
    isActive: isActive,
  });

  return (
    <div
      className={classNames(
        "gg-relative gg-grid gg-cursor-pointer gg-place-items-center gg-rounded gg-border gg-transition gg-duration-300 gg-ease-in-out hover:gg-scale-[1.015] hover:gg-border-[#9493A3]",
        className,
        {
          "gg-border-[#9493A3] gg-bg-[#fff]": isActive,
          "gg-border-[#EAE9ED] gg-bg-white": !isActive,
          "gg-border-dash-2 gg-border-dashed": selectedDoor === "zelfkiezen",
        },
        elementSize,
      )}
    >
      {isActive && (
        <Icon
          name={IconTypes.WHITE_CHECK}
          className="gg-absolute -gg-top-2.5 gg-left-full gg-z-10 gg-aspect-square gg-w-5 -gg-translate-x-2.5 gg-rounded-full gg-bg-green-500 gg-p-1.5"
        />
      )}
      <div className="gg-scale-60 sm:gg-scale-85">{clonedIcon}</div>
    </div>
  );
};

export default DoorOptionsIcon;
